import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { TableAction } from '@app/shared/types/ui';

@Component({
  selector: 'app-table-actions',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, MatMenuModule],
  template: `
    <div class="table-actions">
      @for (action of actions(); track action.id) {
        @if (action.visible !== false) {
          @switch (action.type) {
            @case ('icon') {
              <button
                [id]="action.id"
                mat-icon-button
                [color]="action.color"
                [disabled]="action.disabled"
                [matTooltip]="action.tooltip"
                aria-label="{{ action.tooltip || action.label }}"
                [class]="'table-actions__button ' + (action.class || '')"
                (click)="onActionClick(action.id)"
              >
                <mat-icon [class]="'table-actions__icon ' + (action.iconClass || '')">{{ action.icon }}</mat-icon>
              </button>
            }
            @case ('menu') {
              @if ((action.class || '').includes('table-actions__button--more')) {
                <button
                  [id]="action.id"
                  mat-raised-button
                  [matTooltip]="action.tooltip"
                  aria-label="{{ action.label }}"
                  [class]="'table-actions__button ' + (action.class || '')"
                  [matMenuTriggerFor]="menu"
                  aria-haspopup="menu"
                >
                  @if (action.icon) {
                    <mat-icon [class]="'table-actions__icon ' + (action.iconClass || '')">{{ action.icon }}</mat-icon>
                  }
                  <span class="table-actions__label">{{ action.label }}</span>
                </button>
              } @else {
                <button
                  [id]="action.id"
                  mat-raised-button
                  [color]="action.color || 'primary'"
                  [matTooltip]="action.tooltip"
                  aria-label="{{ action.label }}"
                  [class]="'table-actions__button ' + (action.class || '')"
                  [matMenuTriggerFor]="menu"
                  aria-haspopup="menu"
                >
                  @if (action.icon) {
                    <mat-icon [class]="'table-actions__icon ' + (action.iconClass || '')">{{ action.icon }}</mat-icon>
                  }
                  <span class="table-actions__label">{{ action.label }}</span>
                </button>
              }

              <mat-menu #menu="matMenu">
                @if (action.menuContentTemplate) {
                  <ng-container [ngTemplateOutlet]="action.menuContentTemplate" />
                } @else {
                  @for (item of action.menuItems || []; track item.id) {
                    @if (item.visible !== false) {
                      <button
                        aria-label="{{ item.label }}"
                        mat-menu-item
                        [disabled]="item.disabled"
                        (click)="onMenuItemClick(action.id, item.id)"
                      >
                        @if (item.icon) {
                          <mat-icon>{{ item.icon }}</mat-icon>
                        }
                        <span>{{ item.label }}</span>
                      </button>
                    }
                  }
                }
              </mat-menu>
            }

            @default {
              <button
                mat-raised-button
                [id]="action.id"
                [color]="action.color || 'primary'"
                [disabled]="action.disabled"
                [matTooltip]="action.tooltip"
                aria-label="{{ action.label }}"
                [class]="'table-actions__button ' + (action.class || '')"
                (click)="onActionClick(action.id)"
              >
                @if (action.icon) {
                  <mat-icon [class]="'table-actions__icon ' + (action.iconClass || '')">{{ action.icon }}</mat-icon>
                }
                <span>{{ action.label }}</span>
              </button>
            }
          }
        }
      }
    </div>
  `,
  styles: [
    `
      :host {
        --actions-gap: 8px;
        --button-border-radius: 4px;
      }

      .table-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--actions-gap);
        align-items: center;

        @mixin button-variant($bg-color, $text-color: #fff) {
          background-color: $bg-color;
          color: $text-color;
          transition: filter 0.2s;

          &:hover:not(:disabled) {
            filter: brightness(90%);
          }

          &:disabled {
            cursor: default;
            pointer-events: none;
            color: var(--mdc-filled-button-disabled-label-text-color, rgba(0, 0, 0, 0.38));
            background-color: rgba(0, 0, 0, 0.12);
          }
        }

        &__button {
          display: flex;
          align-items: center;
          margin-left: 8px;
          &--more {
            color: var(--accent-500);
            border: 1px solid var(--accent-500);
          }

          &--send {
            @include button-variant(var(--primary-500));
          }

          &--download {
            @include button-variant(var(--success-500, #4caf50));
          }

          &--columns {
            @include button-variant(transparent, var(--primary-500));
            border: 1px solid var(--primary-500);

            &:hover:not(:disabled) {
              background-color: rgba(36, 33, 208, 0.1);
              filter: none;
            }
          }
        }

        &__icon {
          font-size: 18px;
          height: 18px;
          width: 18px;

          &--send {
            transform: rotate(-45deg);
            position: relative;
            top: -3px;
          }

          &--help {
            font-size: 22px;
            height: 22px;
            width: 22px;
            color: var(--primary-500);
          }
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableActionsComponent {
  actions = input.required<TableAction[]>();
  actionClick = output<string>();
  menuItemClick = output<{ actionId: string; itemId: string }>();

  onActionClick(actionId: string): void {
    this.actionClick.emit(actionId);
  }

  onMenuItemClick(actionId: string, itemId: string): void {
    this.menuItemClick.emit({ actionId, itemId });
  }
}
